import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Button, Modal, Form, Input, Space, Typography,
  message, Popconfirm, Empty, Tag, Tooltip, Progress,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, VideoCameraOutlined,
  PlayCircleOutlined, DownloadOutlined, UploadOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SunumlarPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [form] = Form.useForm();
  const { isAdmin } = useAuth();
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/sunumlar');
      const arr = Array.isArray(res.data) ? res.data : res.data.data || [];
      setData(arr);
    } catch {
      message.error('Sunumlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openModal = (item = null) => {
    setEditItem(item);
    setSelectedFile(null);
    setUploadProgress(0);
    if (item) {
      form.setFieldsValue({ baslik: item.baslik, baslikIcerik: item.baslikIcerik });
    } else {
      form.resetFields();
    }
    setModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 500MB üzeri dosyalar için uyarı (nginx client_max_body_size ayarına bağlı)
      if (file.size > 500 * 1024 * 1024) {
        message.warning(`Dosya çok büyük (${(file.size / 1024 / 1024).toFixed(0)} MB). Sunucu yöneticisi nginx client_max_body_size ayarını artırmalı.`);
      }
      setSelectedFile(file);
    }
  };

  const handleSave = async (values) => {
    if (!editItem && !selectedFile) {
      message.error('Lütfen bir dosya seçin');
      return;
    }

    setSaving(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('baslik', values.baslik);
      if (values.baslikIcerik) formData.append('baslikIcerik', values.baslikIcerik);
      if (selectedFile) formData.append('dosya', selectedFile);

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      };

      if (editItem) {
        await api.put(`/sunumlar/${editItem.id}`, formData, config);
        message.success('Sunum güncellendi');
      } else {
        await api.post('/sunumlar', formData, config);
        message.success('Sunum eklendi');
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/sunumlar/${id}`);
      message.success('Sunum silindi');
      fetchData();
    } catch {
      message.error('Silme başarısız');
    }
  };

  const handleDownload = async (item) => {
    try {
      const response = await api.get(`/sunumlar/download/${item.id}`, { responseType: 'blob' });
      const cd = response.headers['content-disposition'] || '';
      let filename = item.baslik || 'sunum';
      const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = cd.match(/filename="([^"]+)"/i);
      if (utf8Match) filename = decodeURIComponent(utf8Match[1]);
      else if (plainMatch) filename = plainMatch[1];
      else if (item.dosyaYolu) {
        const ext = item.dosyaYolu.split('.').pop();
        filename = `${item.baslik || 'sunum'}.${ext}`;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Dosya indirilemedi');
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <VideoCameraOutlined style={{ fontSize: 20, color: '#1677ff' }} />
            <Title level={4} style={{ margin: 0 }}>Sunumlar</Title>
          </Space>
        </Col>
        <Col>
          {isAdmin() && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
              Yeni Sunum Ekle
            </Button>
          )}
        </Col>
      </Row>

      {loading ? (
        <Row gutter={[16, 16]}>
          {[1, 2, 3].map((i) => (
            <Col xs={24} sm={12} lg={8} key={i}>
              <Card loading style={{ borderRadius: 8 }} />
            </Col>
          ))}
        </Row>
      ) : data.length === 0 ? (
        <Card style={{ borderRadius: 8 }}>
          <Empty description="Sunum bulunamadı" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {data.map((item) => (
            <Col xs={24} sm={12} lg={8} key={item.id}>
              <Card
                className="content-card"
                hoverable
                actions={[
                  ...(item.dosyaYolu ? [
                    <Tooltip title="İndir" key="download">
                      <DownloadOutlined onClick={() => handleDownload(item)} />
                    </Tooltip>,
                  ] : []),
                  ...(isAdmin() ? [
                    <Tooltip title="Düzenle" key="edit">
                      <EditOutlined onClick={() => openModal(item)} />
                    </Tooltip>,
                    <Tooltip title="Sil" key="delete">
                      <Popconfirm
                        title="Bu sunumu silmek istiyor musunuz?"
                        onConfirm={() => handleDelete(item.id)}
                        okText="Evet"
                        cancelText="Hayır"
                      >
                        <DeleteOutlined style={{ color: '#ff4d4f' }} />
                      </Popconfirm>
                    </Tooltip>,
                  ] : []),
                ]}
              >
                <Card.Meta
                  avatar={
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: '#e6f4ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PlayCircleOutlined style={{ fontSize: 24, color: '#1677ff' }} />
                    </div>
                  }
                  title={<Text strong>{item.baslik || 'İsimsiz Sunum'}</Text>}
                  description={
                    <Paragraph
                      ellipsis={{ rows: 2 }}
                      style={{ marginBottom: 8, color: '#666' }}
                    >
                      {item.baslikIcerik || 'Açıklama yok'}
                    </Paragraph>
                  }
                />
                {item.dosyaYolu && (
                  <div style={{ marginTop: 8 }}>
                    <Tag color="blue" style={{ fontSize: 11 }}>
                      {item.dosyaYolu.length > 40
                        ? `...${item.dosyaYolu.slice(-40)}`
                        : item.dosyaYolu}
                    </Tag>
                  </div>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={editItem ? 'Sunum Düzenle' : 'Yeni Sunum Ekle'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setSelectedFile(null); setUploadProgress(0); }}
        footer={null}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
          <Form.Item
            name="baslik"
            label="Başlık"
            rules={[{ required: true, message: 'Başlık zorunludur' }]}
          >
            <Input placeholder="Sunum başlığı" />
          </Form.Item>
          <Form.Item name="baslikIcerik" label="Açıklama">
            <TextArea rows={3} placeholder="Sunum açıklaması" />
          </Form.Item>

          <Form.Item label={editItem ? 'Yeni Dosya Seç (opsiyonel)' : 'Dosya Seç'}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                icon={<UploadOutlined />}
                onClick={() => fileInputRef.current?.click()}
                style={{ width: '100%' }}
              >
                {selectedFile ? selectedFile.name : 'Bilgisayardan Dosya Seç'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              {selectedFile && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Seçilen: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Text>
              )}
              {saving && uploadProgress > 0 && (
                <Progress percent={uploadProgress} size="small" status="active" />
              )}
            </Space>
          </Form.Item>

          <Row justify="end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); setSelectedFile(null); }}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editItem ? 'Güncelle' : 'Kaydet'}
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SunumlarPage;
